"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, Award, GraduationCap, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
const content = {
  vi: {
    sectionBadge: "Đội ngũ giảng viên",
    heading: "Gặp gỡ các kỹ sư trực tiếp giảng dạy con bạn.",
    subheading: "Đội ngũ giảng viên của chúng tôi mang kinh nghiệm thực chiến từ phòng Lab, sở hữu bằng cấp Engineering chuyên sâu cùng tỷ lệ vàng 1:6 giữa giảng viên và học viên trong từng dự án chế tạo.",
    stemBadge: "Giảng viên đạt chuẩn STEM",
    viewTrack: "Xem hồ sơ chi tiết",
    placeholderBadge: "Hồ sơ minh họa",
    ctaHeading: "Ba mẹ cần trao đổi thêm về giảng viên hoặc tư vấn xếp lớp?",
    ctaSubheading: "Đặt lịch đánh giá năng lực 1-1 cùng đội ngũ kỹ sư để xác định chính xác lộ trình xuất phát tốt nhất cho con.",
    exploreBtn: "Khám phá chương trình học",
    trialBtn: "Đăng ký học thử miễn phí",
    loading: "Đang tải hồ sơ giảng viên...",
    empty: "Chưa có hồ sơ giảng viên để hiển thị.",
    error: "Không thể tải hồ sơ giảng viên lúc này.",
    retry: "Thử lại"
  },
  en: {
    sectionBadge: "Faculty Directory",
    heading: "Meet the engineers teaching your children.",
    subheading: "Our instructors bring real-world laboratory experience, advanced engineering degrees, and a low 1:6 student-to-mentor ratio to every build.",
    stemBadge: "STEM Accredited Mentor",
    viewTrack: "View mentor profile",
    placeholderBadge: "Illustrative Profile",
    ctaHeading: "Have questions about our mentors or placement?",
    ctaSubheading: "Schedule a 1-on-1 assessment with our engineering team to determine your child's starting track.",
    exploreBtn: "Explore curriculum",
    trialBtn: "Book a free trial",
    loading: "Loading faculty profiles...",
    empty: "No faculty profiles found.",
    error: "Failed to load faculty profiles at this time.",
    retry: "Retry"
  }
};
const accentBarColors = ["bg-primary", "bg-secondary", "bg-accent"];
function FacultyListConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const t = content[lang];
  if (loading) {
    return <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col justify-between rounded-xl border border-border bg-card p-0 overflow-hidden shadow-xs animate-pulse">
            <div className="aspect-[4/3] w-full bg-muted" />
            <div className="p-6 sm:p-7 space-y-4">
              <div className="h-6 w-3/4 rounded-md bg-muted" />
              <div className="h-4 w-1/2 rounded-md bg-muted" />
              <div className="h-14 w-full rounded-md bg-muted" />
              <div className="h-4 w-2/3 rounded-md bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="mt-12 rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
        <p className="text-sm font-medium text-destructive">{t.error}</p>
        <Button variant="link" onClick={() => refetch()} className="mt-3 h-auto p-0 text-xs font-semibold text-primary underline">
          {t.retry}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="mt-12 rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
        <p className="text-base">{t.empty}</p>
      </div>;
  }
  return <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {posts.map((post: any, i: number) => {
      const cardAccentColor = accentBarColors[i % accentBarColors.length];
      const photoUrl = post.acf?.photo || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_instructor_guiding_student_workbench.png";
      const photo = {
        src: photoUrl,
        srcset: "",
        sizes: ""
      };
      const fullName = post.acf?.full_name;
      const roleTitle = post.acf?.role_title;
      const specialty = post.acf?.specialty;
      const bio = post.acf?.bio;
      return <div key={post.id || i} data-index={i} className="flex">
            <Card className="group relative flex flex-col justify-between w-full overflow-hidden border border-border bg-card p-0 rounded-xl transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-lg">
              {/* Cycling accent bar */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 z-10 ${cardAccentColor}`} />

              {/* Portrait Media */}
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                {photo.src && <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={fullName || "Teacher"} loading="lazy" decoding="async" className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105" />}

                {/* Illustrative Profile Placeholder Badge */}
                <div className="absolute top-3.5 right-3.5 z-10">
                  <span className="inline-flex items-center gap-1 rounded-md border border-border/80 bg-card/95 px-2.5 py-1 font-mono text-[11px] font-semibold text-muted-foreground shadow-xs backdrop-blur-xs">
                    {t.placeholderBadge}
                  </span>
                </div>

                {/* Role / Specialty Overlay Chip */}
                {roleTitle && <div className="absolute bottom-3.5 left-3.5">
                    <span className="inline-flex items-center gap-2 rounded-lg border border-secondary/40 bg-card/95 px-3 py-1.5 font-mono text-xs font-semibold text-secondary shadow-sm backdrop-blur-sm">
                      <span className="flex items-center justify-center p-1 rounded-md bg-secondary/15">
                        <Cpu className="size-3.5 text-secondary" />
                      </span>
                      {roleTitle}
                    </span>
                  </div>}
              </div>

              {/* Profile Details */}
              <CardContent className="flex flex-1 flex-col justify-between p-6 sm:p-7">
                <div className="space-y-3">
                  <div>
                    {fullName && <h3 className="font-serif text-xl sm:text-2xl font-bold text-card-foreground">
                        {fullName}
                      </h3>}
                    {roleTitle && <p className="mt-1 font-mono text-xs font-medium uppercase tracking-wide text-secondary">
                        {roleTitle}
                      </p>}
                  </div>

                  {/* Specialty */}
                  {specialty && <div className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                      <GraduationCap className="size-4 shrink-0 text-muted-foreground mt-0.5" />
                      <span className="font-medium leading-tight">
                        {specialty}
                      </span>
                    </div>}

                  {/* Bio */}
                  {bio && <p className="text-sm text-muted-foreground leading-relaxed pt-2 border-t border-border/60">
                      {bio}
                    </p>}
                </div>

                {/* Card Footer Indicator */}
                <div className="mt-6 pt-4 border-t border-border/40 flex items-center justify-between text-xs font-semibold text-foreground/80">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                    <span className="flex items-center justify-center p-0.5 rounded-sm bg-secondary/10">
                      <Award className="size-3.5 text-secondary" />
                    </span>
                    {t.stemBadge}
                  </span>

                  {post.link ? <Link to={post.link} className="text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex items-center font-medium">
                      {t.viewTrack}
                      <ChevronRight className="size-3.5 ml-0.5" />
                    </Link> : <span className="text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex items-center font-medium">
                      {t.viewTrack}
                      <ChevronRight className="size-3.5 ml-0.5" />
                    </span>}
                </div>
              </CardContent>
            </Card>
          </div>;
    })}
    </div>;
}
export default function Team() {
  const prefersReducedMotion = useReducedMotion();
  const [lang, setLang] = React.useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "vi" || saved === "en") return saved;
    }
    return "vi";
  });
  React.useEffect(() => {
    const handleCustomLang = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: "vi" | "en";
      }>;
      if (customEvent.detail?.lang && (customEvent.detail.lang === "vi" || customEvent.detail.lang === "en")) {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLang);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLang);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const t = content[lang];
  return <section id="meet-the-team" data-nav="light" className="relative w-full bg-background py-20 md:py-24 text-foreground overflow-hidden">
      {/* Background Blueprint Grid Line Motif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(var(--foreground)_1px,transparent_1px)] [background-size:32px_32px]" />

      <div className="relative mx-auto max-w-[1440px] px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Section Header & Spec Rail */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-1.5 w-1.5 rounded-xs bg-secondary" />
              <div className="h-px w-10 bg-secondary" />
              <span className="font-mono text-xs font-semibold tracking-wider text-secondary uppercase">
                {t.sectionBadge}
              </span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              {t.heading}
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t.subheading}
            </p>
          </div>

          {/* Floating Decorative Mascot (Static, Non-interactive) */}
          <div className="flex shrink-0 items-center justify-end self-end lg:self-start lg:pt-2 pointer-events-none select-none relative z-10">
            <motion.div animate={prefersReducedMotion ? undefined : {
            y: [0, -8, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: "easeInOut"
          }} className="pointer-events-none select-none">
              <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} className="h-20 sm:h-28 lg:h-32 w-auto bg-transparent object-contain drop-shadow-md pointer-events-none select-none" />
            </motion.div>
          </div>
        </div>

        {/* Dynamic Faculty Grid from WordPress */}
        <PostsProvider wp_query={{
        post_type: "teacher",
        per_page: 12,
        status: ["publish"]
      }}>
          <FacultyListConsumer lang={lang} />
        </PostsProvider>

        {/* Section Footer Callout */}
        <div className="mt-14 sm:mt-16 rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1">
            <h4 className="font-serif text-lg font-semibold text-foreground">
              {t.ctaHeading}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t.ctaSubheading}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="outline" className="w-full sm:w-auto font-medium">
              <Link to="/LearningPathCurriculumPage">
                {t.exploreBtn}
              </Link>
            </Button>
            <Button asChild className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/FreeTrialContactPage">
                {t.trialBtn}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}